import { ChangeDetectorRef, Component, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmEventType, MenuItem } from 'primeng/api';
import { MultiSelect } from 'primeng/multiselect';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { DateTableColumn } from 'src/app/core/models/date-table-column';
import { LaasOption } from 'src/app/core/models/laas-option';
import { FacilityOptions } from 'src/app/core/models/options/facility-options';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { AutoPrintSettingTableDetailData } from '../../interface/auto-print-setting-detail-data';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { AutoPrintSettingService } from 'src/app/features/setting/service/auto-print-setting.service';
import { NotificationSubScriptionService } from 'src/app/core/services/notification-sub-scription.service';
import { FacilityService } from 'src/app/core/services/facility.service';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { deepCopy, isNullOrEmpty, laaSFormatString, nullIfEmptyArray } from 'src/app/core/utils/common-functions';
import { fromEvent, throwError } from 'rxjs';
import { AutoPrintSettingGetDetailRequest } from '../../interface/auto-print-setting-get-detail-request';
import { AddressMaintainanceService } from 'src/app/features/setting/service/address-maintainance.service';
import { SortOrder } from 'src/app/core/interface/sort-order';
import { PageInfo } from 'src/app/core/models/page-info';
import { DataTableSavedBean } from 'src/app/shared/components/data-table/bean/data-table-saved-bean';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { UserType } from 'src/app/core/enums/user-type.enum';
import { RptIdService } from 'src/app/core/services/rpt-id.service';

@Component({
  selector: 'laas-auto-print-setting-detail',
  templateUrl: './auto-print-setting-detail.component.html',
  styleUrls: ['./auto-print-setting-detail.component.scss']
})
export class AutoPrintSettingDetailComponent extends SessionBaseComponent implements OnInit {

  @ViewChild(DataTableComponent) table!: DataTableComponent;
  @ViewChild('detailTable') detailTable!: DataTableComponent;
  queryFlag: boolean = true;

  // AccordionTab Control
  isOpenedSettingSearchFormTab: boolean = true;
  isOpenedSettingDetailTab: boolean = false;

  searchFormGroup: FormGroup;
  searchParams: any;

  // Options
  @Input() facilityOptions: FacilityOptions[] = [];
  docTypeOptions: LaasOption[] = [];
  deliveryTypeOptions: LaasOption[] = [];
  rptIdOptions: LaasOption[] = [];
  orgIdOptions: LaasOption[] = [];
  consigneeKeyOptions: LaasOption[] = [];
  consigneeKeyOptionsMapping: LaasOption[] = [];
  forwarderOptions:LaasOption[]=[];

  editPermission: boolean = false;

  readonly detailPreferenceKey: string = UserPreferenceConstants.AutoPrintSettingTableDetailData;

  isDetailTableLoading: boolean = false;

  columnOfAutoPrintSettingDetail: DateTableColumn[] = [
    { field: 'facility', header: 'AutoPrintSetting.DetailTable.Column.Facility', sort: true },
    { field: 'deliveryType', header: 'AutoPrintSetting.DetailTable.Column.DeliveryType', sort: true },
    { field: 'orgId', header: 'AutoPrintSetting.DetailTable.Column.OrgId', sort: true },
    { field: 'consigneeKey', header: 'AutoPrintSetting.DetailTable.Column.ConsigneeKey', sort: true },
    { field: 'forwarderCode', header: 'AutoPrintSetting.DetailTable.Column.ForwarderCode', sort: true },
    { field: 'docType', header: 'AutoPrintSetting.DetailTable.Column.DocType', sort: true },
    { field: 'rptId', header: 'AutoPrintSetting.DetailTable.Column.RptId', sort: true },
    { field: 'numberOfPrint', header: 'AutoPrintSetting.DetailTable.Column.NumberOfPrint', sort: true },
    { field: 'createUser', header: 'AutoPrintSetting.HeaderTable.Column.CreateUser', sort: true },
    { field: 'createDt', header: 'AutoPrintSetting.HeaderTable.Column.CreateDt', sort: true },
    { field: 'updateUser', header: 'AutoPrintSetting.HeaderTable.Column.UpdateUser', sort: true },
    { field: 'updateDt', header: 'AutoPrintSetting.HeaderTable.Column.UpdateDt', sort: true },
    { field: '__action', header: '動作', sort: false },
  ];

  settingDetailData: AutoPrintSettingTableDetailData[] = [];

  permissionCode: PermissionConstants = PermissionConstants.ViewAutoPrintSetting;
  readonly editPermissionCode: PermissionConstants = PermissionConstants.EditAutoPrintSetting;

  // Table paging info
  pageInfo: PageInfo = {
    pageSize: 5,
    pageIndex: 0,
    totalRecords: 0,
    totalPages: 0,
  };

  sortOrder: SortOrder = {
    property: "SeqNo",
    direction: -1
  };

  constructor(
    private injector: Injector,
    private formBuilder: FormBuilder,
    private dropdownOptionsService: DropdownOptionsService,
    private autoPrintSettingService: AutoPrintSettingService,
    private notificationSubScriptionService: NotificationSubScriptionService,
    private addressMaintainanceService: AddressMaintainanceService,
    private changeDetector: ChangeDetectorRef,
    private facilityService: FacilityService,
    private rptIdService: RptIdService,
  ) {
    super(injector);
    this.searchFormGroup = this.formBuilder.group({
      facility: [this.laasSetting.facility, Validators.required],
      deliveryTypes: [null, Validators.required],
      docTypes: [null, Validators.required],
      orgIds: null,
      consigneeKeys: [null, Validators.required],
      forwarderCode: null
    });

    this.searchParams = {
      consigneeKey: null,
      consigneeCompany: null
    };

    this.deliveryTypeOptions = [
      { label: 'ALL', value: 'ALL'},
      { label: 'LOCAL', value: 'LOCAL' },
      { label: 'EXPORT', value: 'EXPORT' },
    ];

  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    this.editPermission = this.userProfile.userType === UserType.SuperAdmin.toString() || this.permissionCodes.includes(this.editPermissionCode);
    if (this.editPermission) {
      this.permissionCode = this.editPermissionCode;
    }

    this.facilityService.getTenantOrgOptions().subscribe({
      next: (options: any[]) => {
        this.orgIdOptions = options.map(x => ({ label: `${x.orgId} - ${x.orgName}`, value: x.orgId }));
        this.orgIdOptions.push({ label: 'ALL', value: 'ALL' });
      }
    });

    this.addressMaintainanceService.getCustomerOptions().subscribe({
      next: (result) => {
        this.consigneeKeyOptionsMapping = [...result].map((x: any) => {
          return {
            label: `${x.customerCode} - ${x.customerName}`,
            value: x.customerCode,
          };
        });
      },
    });

    this.autoPrintSettingService.getAutoPrintSettingDocTypeOption().subscribe({
      next: (options: any[]) => {
        this.docTypeOptions = options.map(x => ({ label: x, value: x }));
      }
    });

    this.dropdownOptionsService.getForwarderOptions().subscribe({
      next: (result: any[]) => {
        this.forwarderOptions = result.map((x) => ({
          label: x.optionDesc,
          value: x.optionCode,
        }));
        this.forwarderOptions.unshift({label: "ALL", value: "ALL"});
      }
    });
  }

  resetBtnClick() {
    this.searchFormGroup.reset();
    this.searchFormGroup.controls['facility'].setValue(this.laasSetting.facility);
    this.searchFormGroup.controls['deliveryTypes'].setValue(null);
    this.searchFormGroup.controls['docTypes'].setValue(null);
    this.searchFormGroup.controls['orgIds'].setValue(null);
    this.searchFormGroup.controls['consigneeKeys'].setValue(null);
    this.searchFormGroup.controls['forwarderCode'].setValue(null);
    this.pageInfo = {
      pageSize: 5,
      pageIndex: 0,
      totalRecords: 0,
      totalPages: 0,
    }
  }

  searchBtnClick(): void {
    this.detailTable.loading = true;
    this.detailTable.cancelRowEdit();
    this.detailTable.isInEditMode = false;
    this.detailTable.setCurrentPage(0);
    this.isOpenedSettingDetailTab = true;
    this.pageInfo = {
      pageSize: 5,
    pageIndex: 0,
    totalRecords: 0,
    totalPages: 0,
    }
    this.getDetailTableData();
  }

  createGetDetailRequest(): AutoPrintSettingGetDetailRequest {
    let detailRequest: AutoPrintSettingGetDetailRequest = new AutoPrintSettingGetDetailRequest();
    detailRequest.facility = this.searchFormGroup.value.facility;
    detailRequest.deliveryTypes = nullIfEmptyArray(this.searchFormGroup.value.deliveryTypes);
    detailRequest.docTypes = nullIfEmptyArray(this.searchFormGroup.value.docTypes);
    detailRequest.orgIds = nullIfEmptyArray(this.searchFormGroup.value.orgIds);
    detailRequest.consigneeKeys = nullIfEmptyArray(this.searchFormGroup.value.consigneeKeys);
    detailRequest.forwarderCodes = nullIfEmptyArray(this.searchFormGroup.value.forwarderCode);
    return detailRequest;
  }

  getDetailTableData(): void {
    let request: any = this.createGetDetailRequest();
    request.pageIndex = this.pageInfo.pageIndex,
    request.pageSize = this.pageInfo.pageSize,
    request.sortOrder = this.sortOrder

    this.isDetailTableLoading = true;

    this.autoPrintSettingService.getAutoPrintSettingDetail(request)
      .pipe(finalize(() => (this.isDetailTableLoading = false)))
      .subscribe({
        next: (response: any) => {
          if (isNullOrEmpty(response)) {
            throwError(response);
          }

          this.isOpenedSettingSearchFormTab = false;
          this.settingDetailData = [];

          //Update table
          if (!isNullOrEmpty(response.content)) {
            this.settingDetailData = deepCopy(response.content) ?? [];
          }
          this.logger.info('settingDetailData=', JSON.stringify(this.settingDetailData));

          this.detailTable.updateTableData(this.settingDetailData);
          if (this.detailTable.selectedRow) {
            this.onRowSelect(this.detailTable.selectedRow);
          }

          // Change page Info
          this.pageInfo.totalRecords = response.totalElements;
          this.detailTable.setPageInfo(this.pageInfo.totalRecords);
        },
        error: (response) => {
          this.settingDetailData = [];
          this.detailTable.updateTableData(this.settingDetailData);
          this.pageInfo.totalRecords = 0;
          this.pageInfo.totalPages = 0;
          this.detailTable.setPageInfo(this.pageInfo.totalRecords);
          this.toastService.error(this.translateService.instant('Common.Toast.GetDataFail') + response.status);
        },
        complete: () => {
          this.logger.info('complete...');
          this.detailTable.loading = false;
        },
      });
  }

  getCustomerOptions(event: any) {
    const conText: string = event.filter;
    if (conText == "ALL") {
      this.updateConsigneeKeyOptions([{
        label: "ALL - ALL",
        value: "ALL",
      }]);
    } else if (conText != null && conText.length > 0 && this.queryFlag) {
      this.queryFlag = false;
      this.notificationSubScriptionService.getCustomerOptionsByFilter(conText).subscribe({
        next: (options: any[]) => {
          this.updateConsigneeKeyOptions(options.map<LaasOption>(x => ({
            label: x.customerCode + ' - ' + x.customerName,
            value: x.customerCode,
          })));
          this.queryFlag = true;
        }
      });
    }
  }

  private updateConsigneeKeyOptions(options: LaasOption[]) {
    this.consigneeKeyOptions = options;
    this.changeDetector.detectChanges();
  }

  onInputHandle(event:any, rowData: any) {
    rowData.numberOfPrint = event.value;
  }

  exportBtnClick(event: any) {
    let request: AutoPrintSettingGetDetailRequest = this.createGetDetailRequest();

    this.loader.show();
    this.autoPrintSettingService.exportAutoPrintSettingDetail(request).subscribe({
      next: result => {

        if (result == null || result.size == 0) {
          this.translateService.get('Common.Toast.NoData').subscribe({
            next: i18nResult => {
              this.toastService.info(i18nResult);
            }
          });
          this.loader.hide();
        } else {
          const file = result;

          this.translateService.get("AutoPrintSettingDetail_Report").subscribe({
            next: i18nResult => {
              file.name = (new Date().toLocaleDateString() + "_" + i18nResult + ".xlsx");
            }
          });

          const url = URL.createObjectURL(file);
          let a: any = document.createElement('a');
          a.href = url
          a.download = file.name;
          a.click();
          URL.revokeObjectURL(url);
        }
      },
      complete: () => {
        this.loader.hide();
      }
    })
  }

  deleteBtnClick(seqNo: number) {
    this.confirmationService.confirm({
        message: this.translateService.instant('將移除資料庫資料，請問要刪除嗎?'),
        header: this.translateService.instant('系統資訊'),
        icon: 'pi pi-info-circle',
        accept: () => {
          this.loader.show();
          this.autoPrintSettingService.deleteAutoPrintSettingDetail(seqNo).subscribe({
            next: result => {
              this.toastService.success('資料刪除完成!!');
              this.searchBtnClick();
            },
            error: async response => {
              this.loader.hide();
              this.detailTable.loading = false;
              var instantMsg = await this.translateService.get("Common.Toast.SaveFail").toPromise();
              var msg = `${laaSFormatString(instantMsg, [response.status, response.error.errorMsg])}\n`;
              this.toastService.error(msg);
            },
            complete: () => {
              this.logger.info('complete...');
              this.loader.hide();
              this.detailTable.loading = false;
            }
          });
        },
        reject: (type: any) => {
          switch (type) {
            case ConfirmEventType.REJECT:
              break;
            case ConfirmEventType.CANCEL:
              break;
          }
        }
      });
  }

  createNewRow(rowId: number): any {
    let newRow = {
      seqNo: null,
      facility: this.laasSetting.facility,
      deliveryType: null,
      rptId: null,
      orgId: null,
      consigneeKey: null,
      forwarderCode: null,
      docType: null,
      numberOfPrint: null,
      createUser: null,
      createDt: null,
      updateUser: null,
      updateDt: null,
      rowId: rowId,
    };
    return newRow;
  }

  dataInputValid(rowData: AutoPrintSettingTableDetailData): boolean {
    if (rowData) {
      if(isNullOrEmpty(rowData.deliveryType) && isNullOrEmpty(rowData.rptId)  && isNullOrEmpty(rowData.orgId) &&
          isNullOrEmpty(rowData.docType) && isNullOrEmpty(rowData.numberOfPrint) && isNullOrEmpty(rowData.consigneeKey &&
          isNullOrEmpty(rowData.forwarderCode))){
            return true;
      }

      if (isNullOrEmpty(rowData.deliveryType) || isNullOrEmpty(rowData.rptId)  || isNullOrEmpty(rowData.orgId) ||
          isNullOrEmpty(rowData.docType) || isNullOrEmpty(rowData.numberOfPrint) || isNullOrEmpty(rowData.consigneeKey ||
            isNullOrEmpty(rowData.forwarderCode))) {
        this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFields'));
        return false;
      }
    }
    return true;
  }

  createRow(rowData: any): void {
    this.onRowSelect(rowData);
  }

  onRowSelect(event: any): void {
    console.log("onRowSelect-event=", JSON.stringify(event));
  }

  saveData(savedDataBean: DataTableSavedBean): void {
    const saveDataRequest = {
      insertList: savedDataBean.newList.filter(rowData =>
        !(
          isNullOrEmpty(rowData.deliveryType) &&
          isNullOrEmpty(rowData.rptId) &&
          isNullOrEmpty(rowData.orgId) &&
          isNullOrEmpty(rowData.docType) &&
          isNullOrEmpty(rowData.numberOfPrint) &&
          isNullOrEmpty(rowData.consigneeKey) &&
          isNullOrEmpty(rowData.forwarderCode)
        )
      ),
      updateList: savedDataBean.updatedList.filter(rowData =>
        !(
          isNullOrEmpty(rowData.deliveryType) &&
          isNullOrEmpty(rowData.rptId) &&
          isNullOrEmpty(rowData.orgId) &&
          isNullOrEmpty(rowData.docType) &&
          isNullOrEmpty(rowData.numberOfPrint) &&
          isNullOrEmpty(rowData.consigneeKey) &&
          isNullOrEmpty(rowData.forwarderCode)
        )
      ),
      updateUser: this.userProfile.userName
    };
    this.autoPrintSettingService.saveAutoPrintSettingDetail(saveDataRequest).subscribe({
      next: () => {
        this.detailTable.saveSuccessEvent();
        this.searchBtnClick();
        this.toastService.success(this.translateService.instant('Common.Toast.SaveSuccess'));
      },
      error: async response => {
        this.detailTable.loading = false;
        var instantMsg = await this.translateService.get("Common.Toast.SaveFail").toPromise();
        var msg = `${laaSFormatString(instantMsg, [response.status, response.error.errorMsg])}\n`;
        this.toastService.error(msg);
      }, complete: () => {
        this.logger.info('complete...');
        this.detailTable.loading = false;
      }
    });
  }

  restoreData(): void {
    this.ngOnInit();
  }

  tablePageChange(event: any): void {
    this.pageInfo.pageSize = event.rows;
    this.pageInfo.pageIndex = event.first / this.pageInfo.pageSize;
    this.getDetailTableData();
  }

  tableSort(event: any): void {
    this.sortOrder = { property: event.field, direction: event.order };
    this.detailTable.setCurrentPage(0);
    this.getDetailTableData();
  }

  updateRptIdOptions(docType : any, rowData : any) {
    this.rptIdOptions = [];
    rowData['rptId'] = '';

    //獲取rptOption
    this.rptIdService.getRptOptions(docType).subscribe({
      next: (options: any[]) => {

        this.rptIdOptions = options.map(x => ({ label: x.rptId, value: x.rptId }));
      }
    });
  }

  generateRptIdOptions(docType : any) {
    //獲取rptOption
    this.rptIdService.getRptOptions(docType).subscribe({
      next: (options: any[]) => {

        this.rptIdOptions = options.map(x => ({ label: x.rptId, value: x.rptId }));
      }
    });
  }
}
